<template>
    <div class="clock-and-date-widget flex flex-col items-center justify-center h-full w-full">
        <div class="text-center">
            <div
                v-if="props.data?.WidgetLayout?.show_time"
                :style="{
                    fontSize: `${timeFontSize}px`,
                }"
                class="current-time font-semibold mb-2"
            >
                {{ currentTime }}
            </div>
            <div
                v-if="props.data?.WidgetLayout?.show_date"
                :style="{
                    fontSize: `${dateFontSize}px`,
                }"
                class="current-date"
            >
                {{ currentDate }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { WidgetList } from '@/types/dashboard'
import { getDashboardGMTConfig } from '@/api/widgetDataApi'
import { GmtOffsetTypes } from '@/enum/gmt'
import { getEntityList } from '@/composables/globalData/useEntitiesData'
import { DEFAULT_TIME_FORMAT, DEFAULT_DATE_FORMAT } from '@/enum/dateTimeFormats'
import { dayjs } from '@/composables/useDayjs'
import { useSynchronizedClock } from '@/composables/useSynchronizedClock'

interface IClockAndDateWidgetProps {
    data: WidgetList
    editable?: boolean
    onEditMode?: boolean
}

const props = withDefaults(defineProps<IClockAndDateWidgetProps>(), {
    editable: false,
    onEditMode: false
})

const currentTime = ref<string>('')
const currentDate = ref<string>('')

const gmtConfig = computed(() => getDashboardGMTConfig())

const DEFAULT_TIME_FONT_SIZE = 36
const DEFAULT_DATE_FONT_SIZE = 20

const timeFontSize = computed(() => {
    return props.data?.WidgetLayout?.valueFontSize || DEFAULT_TIME_FONT_SIZE
})

const dateFontSize = computed(() => {
    return props.data?.WidgetLayout?.titleFontSize || DEFAULT_DATE_FONT_SIZE
})

const timeFormat = computed(() => {
    return props.data?.WidgetLayout?.timeFormat || DEFAULT_TIME_FORMAT
})

const dateFormat = computed(() => {
    return props.data?.WidgetLayout?.dateFormat || DEFAULT_DATE_FORMAT
})

function updateDateTime () {
    const config = gmtConfig.value
    
    if (!config) {
        const now = dayjs()
        if (props.data?.WidgetLayout?.show_time) {
            currentTime.value = now.format(timeFormat.value)
        }
        if (props.data?.WidgetLayout?.show_date) {
            currentDate.value = now.format(dateFormat.value)
        }
        return
    }
    
    if (config.Type === GmtOffsetTypes.Browser) {
        const now = dayjs()
        if (props.data?.WidgetLayout?.show_time) {
            currentTime.value = now.format(timeFormat.value)
        }
        if (props.data?.WidgetLayout?.show_date) {
            currentDate.value = now.format(dateFormat.value)
        }
        return
    }

    if (config.Type === GmtOffsetTypes.DashboardTimezone && config.GMTOffset) {
        const now = dayjs()
        if (props.data?.WidgetLayout?.show_time) {
            currentTime.value = now.tz(config.GMTOffset).format(timeFormat.value)
        }
        if (props.data?.WidgetLayout?.show_date) {
            currentDate.value = now.tz(config.GMTOffset).format(dateFormat.value)
        }
        return
    }
    
    if (config.Type === GmtOffsetTypes.Account && config.AccountID) {
        const allAccounts = getEntityList('Accounts')
        const account = allAccounts.find(acc => acc.AccountID === Number(config.AccountID))
        
        if (account?.GmtOffset !== undefined) {
            const offsetMs = account.GmtOffset * 1000
            const utcTime = Date.now()
            const browserOffsetMs = new Date().getTimezoneOffset() * 60 * 1000
            const accountTime = new Date(utcTime + browserOffsetMs + offsetMs)
            const dayjsTime = dayjs(accountTime)
            if (props.data?.WidgetLayout?.show_time) {
                currentTime.value = dayjsTime.format(timeFormat.value)
            }
            if (props.data?.WidgetLayout?.show_date) {
                currentDate.value = dayjsTime.format(dateFormat.value)
            }
            return
        }
    }
}

onMounted(() => {
    useSynchronizedClock(updateDateTime)
})
</script>

<style lang="scss" scoped>
.clock-and-date-widget {
    .current-time {
        color: var(--colorWidgetHeaderText-color, var(--mainTextColor-color));
    }
    
    .current-date {
        color: var(--colorWidgetTimeText-color, var(--mainTextColor-color));
    }
}
</style>
